import axios, { AxiosResponse } from 'axios';

import { EZadostPravoZastupuje } from 'services/spravaEmitenta/zadostPravo';

import {
    EPodTypDokumentu,
    EPodTypdokumentuPrikaz,
    EStavPodpisu,
    EStavZadostiDok,
    ETypDokumentu
} from 'enums/dokumentyEnums';
import { EDruhCp } from 'enums/isin/udaje/druhCp';

import getLanguageObject from 'utils/getLanguageObject';

import { ISeznamPrikaz } from './prikazy';

export interface IDokumentyPrehledInput {
    typ?: ETypDokumentu;
    podtyp?: EPodTypDokumentu;
    stav?: EStavZadostiDok;
    aktivni?: boolean;
    order: string;
    limit: number;
    offset: number;
}

export interface ISmlouva {
    iddok: number;
    podtyp: EPodTypDokumentu;
    elverze: boolean;
    nazevsouboru: string;
}
export interface IDokument {
    iddok: number;
    idosoby?: number;
    typ?: ETypDokumentu;
    podtyp?: EPodTypDokumentu;
    stav?: EStavZadostiDok;
    stavpodpisu?: EStavPodpisu;
    vyjadrenicdcp?: string;
    zmenadatum?: string;
    isin?: string;
    druhcp?: string;
    nazevsouboru?: string;
    typpodpisu?: string;
}

export interface IDokumentyPrehled {
    count: number;
    order: string;
    limit: number;
    offset: number;
    dokumenty: IDokument[];
    smlouvy: ISmlouva[];
}

export const dokumentPrehledApi = (params?: IDokumentyPrehledInput) => {
    return axios({
        method: 'GET',
        url: '/DOK/prehled',
        params: params
    }).then((resp: AxiosResponse<IDokumentyPrehled>) => resp.data);
};

interface IDokumentPravaObj {
    isin: string;
    data: string;
    druhcp: EDruhCp;
}

export interface IDokumentyPrava {
    isin: boolean;
    smlouva: boolean;
    smlouvadlt: boolean;
    dodatek: IDokumentPravaObj[];
    dodatekdlt: IDokumentPravaObj[];
    navyseni: IDokumentPravaObj[];
    navysenidlt: IDokumentPravaObj[];
    prikazy: {
        typprikazu: EPodTypdokumentuPrikaz;
        emise: IDokumentPravaObj[];
        sezupis?: ISeznamPrikaz[];
    }[];
}

export const dokumentIsinApi = () => {
    return axios({
        method: 'GET',
        url: '/DOK/zadostoisin'
    }).then((resp: AxiosResponse<IDokumentyPrava>) => resp.data);
};
export const dokumentVyjadreniApi = (id: number | string, dokJiny?: boolean) => {
    return axios({
        method: 'GET',
        url: `/${dokJiny ? 'DOKjiny' : 'DOK'}/${id}/vyjadreni`
    }).then(
        (
            resp: AxiosResponse<{
                vyjadreni: string;
            }>
        ) => resp.data
    );
};

export const dokumentZmenaStavuApi = (id: number | string, stav: EStavZadostiDok, dokJiny?: boolean) => {
    return axios({
        method: 'PUT',
        url: `/${dokJiny ? 'DOKjiny' : 'DOK'}/${id}/stav`,
        data: {
            stav: stav
        }
    });
};

export const dokumentDownload = (id: number | string, nazevsouboru: string) => {
    return axios({
        method: 'GET',
        url: `/DOK/${id}/stahnout`,
        responseType: 'blob' // important
    }).then((resp: { status: number; data: any }) => {
        if (resp.status === 200) {
            // způsob, jak stažený dokument otevřít v browseru
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const nav = window.navigator as any;
            // IE
            if (nav?.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(resp.data, nazevsouboru);
            } else {
                const odp = resp.data;

                const uriContent = URL.createObjectURL(
                    new Blob([odp], {
                        type: 'application/octetstream'
                    })
                );
                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = nazevsouboru;
                link.href = uriContent;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    });
};

export interface IDokOsoby {
    osoby: IDokOsoba[];
    cdcpzast: {
        jmeno: string;
        funkce: string;
        typzast: string;
    }[];
    aktualizujosoby: boolean;
}

export interface IDokOsoba {
    c2dokosobaoid: number;
    poradi: number;
    podepisuje: boolean;
    stavpodpisu: EStavPodpisu;
    typpodpisu: 'CERT' | 'BI';
    datdanokpodp: string;
    podepsano: string;
    ceriss?: string;
    cersn?: string;
    cerdn?: string;
    bisub?: string;
    podpisover?: boolean;
    id: number;
    uzivateloid: number;
    zadostoid: number;
    jmeno: string;
    prijmeni: string;
    email: string;
    telefon: string;
    emitentazas: EZadostPravoZastupuje;
    fileoid: number;
}

export const dokumentZalozeniApi = (data: { typ: string; podtyp: string }) => {
    return axios({
        method: 'POST',
        url: '/DOK/dokument',
        data: data
    }).then((resp: AxiosResponse<{ id: number; stav: string }>) => resp.data);
};

export interface ISmlouvniStrany {
    cdcp: {
        idmb: string;
        dicdph: string;
        nazev: string;
        lei: string;
        adma: string;
        mema: string;
        pscm: string;
        kost: string;
        predcisli: string;
        cislouctu: string;
        banka: string;
        iban: string;
    };
    spolecnost: {
        idmb: string;
        dicdph: string;
        nazev: string;
        lei: string;
        adma: string;
        mema: string;
        pscm: string;
        kost: string;
        predcisli: string;
        cislouctu: string;
        banka: string;
        iban: string;
        nace: string;
    };
}
export const dokumentSmlouvniStranyGet = (id: number | string) => {
    return axios({
        method: 'GET',
        url: `/DOK/${id}/smluvnistrany`
    }).then((resp: AxiosResponse<ISmlouvniStrany>) => resp.data);
};

export const dokumentSmlouvniStranyPut = (
    id: number | string,
    data: { predcisli?: string; cislouctu: string; banka: string; iban?: string }
) => {
    return axios({
        method: 'PUT',
        url: `/DOK/${id}/smluvnistrany`,
        data: data
    });
};
export const dokumentOsobyApi = (id: string | number, podepisuje?: boolean) => {
    return axios({
        method: 'GET',
        url: `/DOK/${id}/osoby`,
        ...(podepisuje && {
            params: {
                podepisuje: !!podepisuje
            }
        })
    }).then((resp: AxiosResponse<IDokOsoby>) => resp.data);
};
export const dokumentOsobyUpdateApi = (id: string | number) => {
    return axios({
        method: 'POST',
        url: `/DOK/${id}/aktosoby`
    }).then((resp: AxiosResponse<IDokOsoby>) => resp.data);
};

export const dokumentOsobyPDFdownload = (
    iddok: string | number,
    idosoba: string | number,
    nazev: string,
    isJinaOsoba?: boolean
) => {
    return axios({
        method: 'GET',
        url: `/${isJinaOsoba ? 'DOKjiny' : 'DOK'}/${iddok}/osoba/${idosoba}/pdfdownload`,
        responseType: 'blob' // important
    }).then((resp: { status: number; data: any }) => {
        if (resp.status === 200) {
            // způsob, jak stažený dokument otevřít v browseru
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const nav = window.navigator as any;
            // IE
            if (nav?.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(resp.data, nazev);
            } else {
                const odp = resp.data;

                const uriContent = URL.createObjectURL(
                    new Blob([odp], {
                        type: 'application/octetstream'
                    })
                );
                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = nazev;
                link.href = uriContent;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    });
};

export const dokumentOsobyPodpis = (iddok: string | number, idosoba: string | number, val: boolean) => {
    return axios({
        method: 'PUT',
        url: `/DOK/${iddok}/osoba/${idosoba}/podepisovani`,
        data: {
            podepisuje: val
        }
    }).then((resp: AxiosResponse<IDokOsoby>) => resp.data);
};
export const odeslatKpodpisu = (iddok: string | number, idosoba: string | number) => {
    return axios({
        method: 'PUT',
        url: `/DOK/${iddok}/osoba/${idosoba}/odeslikpodp`
    }).then((resp: AxiosResponse<{ stav: string }>) => resp.data);
};
export const banksignPodpisRedirect = (iddok: string | number, idosoba: string | number) => {
    return axios({
        method: 'PUT',
        url: `/DOK/${iddok}/osoba/${idosoba}/banksign`
    }).then((resp: AxiosResponse<{ redirect: string }>) => resp.data);
};
export const dokumentOsobyPodpisPDF = (idsoubor: string | number, language: string) => {
    return axios({
        method: 'GET',
        url: `/DOK/pdfdownload/${idsoubor}`,
        responseType: 'blob' // important
    }).then((resp: { status: number; data: any }) => {
        if (resp.status === 200) {
            // způsob, jak stažený dokument otevřít v browseru
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const nav = window.navigator as any;
            // IE
            if (nav?.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(resp.data, `${getLanguageObject(language).TYP_DOKUMENTU_SMLUVA}.pdf`);
            } else {
                const odp = resp.data;

                const uriContent = URL.createObjectURL(
                    new Blob([odp], {
                        type: 'application/octetstream'
                    })
                );
                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = `${getLanguageObject(language).TYP_DOKUMENTU_SMLUVA}.pdf`;
                link.href = uriContent;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    });
};
export const dokumentOsobyPodpisPUT = (idZadost: string | number, idOsoba: string | number, tempnazev: string) => {
    return axios({
        method: 'PUT',
        url: `/DOK/${idZadost}/osoba/${idOsoba}/elpodpis`,
        data: {
            tempnazev: tempnazev
        }
    }).then((resp: AxiosResponse<{ stav: string }>) => resp.data);
};
